import { EventType } from "@/analytics/analytics-events";
import type { DiscardManualBlursProperties } from "@/analytics/manual-blurring-events-properties";
import { useDetectUnmountOrDeactivation } from "@/hooks/use-detect-unmount-or-deactivation";
import { useAppDispatch } from "@/store/store-hooks";
import { deactivateTool } from "@/store/ui/ui-slice";
import { Alert, FaroText, useDialog } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Stack } from "@mui/system";
import { useCallback } from "react";
import { useBeforeUnload } from "react-router-dom";
import {
  selectHasManualBlurringStoreUnsavedChanges,
  useManualBlurringStore,
  useResetManualBlurringStore,
} from "./manual-blurring-store";
import { useApplyManualBlurring } from "./use-apply-manual-blurring";

/**
 * Hook that should be used by all components using the manual blurring store.
 * It will initialize the store when the tool activates, and notify the user about unsaved changes on unmount.
 *
 * @param isToolActive Whether the manual blurring tool is active
 */
export function useDetectManualBlurringMount(isToolActive: boolean): void {
  const resetManualBlurringStore = useResetManualBlurringStore();
  const applyManualBlurring = useApplyManualBlurring();

  const dispatch = useAppDispatch();

  const { createDialog } = useDialog();

  const showSaveDialog = useCallback(async () => {
    Analytics.track(EventType.leaveManualBlurringUnintentionally);

    const hasConfirmed = await createDialog({
      title: "Save changes?",
      content: (
        <Stack sx={{ gap: 2 }}>
          <FaroText variant="bodyM">
            You have unsaved manual blurs. Do you want to permanently apply them
            before leaving this mode?
          </FaroText>

          <Alert variant="warning" title="The operation cannot be undone" />
        </Stack>
      ),
      cancelText: "Discard",
      confirmText: "Save changes",
    });

    if (hasConfirmed) {
      applyManualBlurring(true, "save changes dialog");
    } else {
      Analytics.track<DiscardManualBlursProperties>(
        EventType.discardManualBlurs,
        { via: "save changes dialog" },
      );

      resetManualBlurringStore();
      dispatch(deactivateTool());
    }
  }, [applyManualBlurring, createDialog, dispatch, resetManualBlurringStore]);

  // Prevent the browser from closing if there are unsaved changes
  const onBeforeUnload = useCallback(
    (ev: BeforeUnloadEvent) => {
      if (
        selectHasManualBlurringStoreUnsavedChanges(
          useManualBlurringStore.getState(),
        )
      ) {
        ev.preventDefault();
        // All browsers prevent custom messages, so we just have to pass an empty string here to get the dialog to appear.
        ev.returnValue = "";
        showSaveDialog();
      }
    },
    [showSaveDialog],
  );
  useBeforeUnload(onBeforeUnload);

  const onUnmountOrDeactivation = useCallback(() => {
    if (
      selectHasManualBlurringStoreUnsavedChanges(
        useManualBlurringStore.getState(),
      )
    ) {
      showSaveDialog();
    } else {
      resetManualBlurringStore();
    }
  }, [resetManualBlurringStore, showSaveDialog]);
  useDetectUnmountOrDeactivation(isToolActive, onUnmountOrDeactivation);
}
